import React from 'react'
import {RideList} from './RideList'
import { connect } from 'react-redux'
import _ from 'lodash'
import moment from 'moment'
import smartDate from '@qrohlf/smart-date'
import styles from './RidePicker.scss'
import {Button} from 'common/Button'
import {searchRides} from 'lib/searchUtils.js'
import {CircleSpinner} from 'common/spinners'
import {Icon} from 'common/LegacyIcon'
import {Map} from 'immutable'

export class RidePicker extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      keywords: '',
      searchResults: null,
      searchLoading: null,
      selectedRides: props.selectedRides || Map()
    }
    this.throttledSearch = _.throttle(this.doSearch, 500, {leading: false, trailing: true})
  }

  componentDidMount () {
    this.doSearch({sort_by: 'created DESC'})
  }

  doSearch = () => {
    const {currentUser} = this.props
    const {keywords} = this.state
    const searchSlug = `${currentUser}/${keywords}`
    const sortBy = keywords ? 'rank DESC' : 'created DESC'
    this.setState({searchLoading: searchSlug})
    searchRides({
      userId: currentUser.id,
      trips: true,
      routes: false,
      public: true,
      is_gps: true,
      keywords,
      sortBy
    }).then(response => {
      // guard against race conditions
      if (this.state.searchLoading === searchSlug) {
        // filter out non-gps routes
        const results = response.results.filter(t => !t.trip.is_stationary)
        this.setState({searchLoading: null, searchResults: results})
      }
    })
  }

  onKeywordChange = (e) => {
    this.setState({keywords: e.target.value}, this.throttledSearch)
  }

  onRideClick = (ride) => {
    const {selectedRides} = this.state
    if (selectedRides.get(ride.id)) {
      this.setState({selectedRides: selectedRides.delete(ride.id)})
    } else {
      this.setState({selectedRides: selectedRides.set(ride.id, ride)})
    }
  }

  confirmSelection = () => {
    const {onConfirmSelection} = this.props
    const {selectedRides} = this.state
    onConfirmSelection(selectedRides)
  }

  render () {
    const {searchResults, keywords, searchLoading, selectedRides} = this.state
    const initialSelected = this.props.selectedRides

    const ridesRemoved = initialSelected && !initialSelected.keySeq().isSubset(selectedRides.keySeq())

    if (!searchLoading && keywords === '' && searchResults && searchResults.length === 0) {
      return noRidesMessage
    }

    return (
      <div>
        <div className={styles.searchContainer}>
          <input className={styles.searchBar}
            type='text'
            placeholder='search your rides'
            value={keywords}
            onChange={this.onKeywordChange} />
          {searchLoading && <CircleSpinner size={25} className={styles.searchLoading} />}
        </div>
        {searchResults && <RideList
          rides={searchResults.map(r => r.trip)}
          selected={selectedRides}
          onRideClick={this.onRideClick} />}
        {searchResults && searchResults.length === 0 && noResultsMessage}
        {searchResults === null && <CircleSpinner size={40} />}
        <h2 className={styles.subheading}>Selected Rides:</h2>
        <div>
          {selectedRides.map((ride, k) => (
            <div key={k} className={styles.selectedRide}>
              {ride.name} <Icon i='x' onClick={() => this.onRideClick(ride)} style={{cursor: 'pointer', marginLeft: 5}} />
            </div>
          )).toIndexedSeq().toJS()}
        </div>
        <p className={styles.hint}>click one or more of the rides in the list above to add it to your report</p>
        {ridesRemoved && <p className={styles.warning}>You have selected to remove some rides from this report. If you remove these rides, all the maps and climbs associated with the rides will be removed as well.</p>}
        {selectedRides.size > 0 && <Button onClick={this.confirmSelection} className={styles.goButton} type='success'>Go <Icon i='rightChevron' size={15} /></Button>}
      </div>
    )
  }
}

export const noResultsMessage = <div className={styles.infoMessage}>
  <div className={styles.multibike}>
    <Icon i='rwgps' size={50} />
    <Icon i='rwgps' size={50} />
    <Icon i='rwgps' size={50} />
    <Icon i='rwgps' size={50} style={{opacity: 0.25}} />
    <Icon i='rwgps' size={50} />
  </div>
  <h3>No results</h3>
  <p>
    We couldn't find any rides that matched that search. Try a different query, or looking at your <a href='/activities'>activities feed</a> to make sure the ride you're looking for exists. Only public routes with GPS data are supported at this time.
  </p>
</div>

export const noRidesMessage = <div className={styles.infoMessage}>
  <div className={styles.multibike}>
    <Icon i='rwgps' size={50} />
    <Icon i='rwgps' size={50} />
    <Icon i='rwgps' size={50} />
    <Icon i='rwgps' size={50} style={{opacity: 0.25}} />
    <Icon i='rwgps' size={50} />
  </div>
  <h3>No Public Rides</h3>
  <p>
    To write a ride report, you need to record or upload some rides first!
  </p>
  <p>
    You can do that with our <a href='/app'>iOS and Android apps</a>, or by
    uploading a gps file on the <a href='/log'>upload page</a>.
  </p>
  <p>
    Currently, you can only create a Ride Report from public rides,
    so if you have uploaded a ride but it's not showing on this page try checking
    to make sure that the ride visibility is public.
  </p>
</div>

const niceListString = (arr, oxfordComma = false) => {
  if (arr.length <= 2) {
    const comma = oxfordComma ? ',' : ''
    return arr.join(`${comma} and `)
  }
  return arr[0] + ', ' + niceListString(arr.slice(1), true)
}

export const getTitle = rides => {
  if (rides.size === 1) {
    const first = rides.valueSeq().first()
    const title = first.name
    const subtitle = smartDate(first.departed_at)
    return {title, subtitle}
  } else {
    const months = rides.valueSeq().map(r => moment(r.departed_at))
      .sort((a, b) => a > b)
      .map(d => d.format('MMMM')).toJS()
    const monthString = niceListString(_.sortedUniq(months))
    const title = `${rides.size} rides in ${monthString}`
    const subtitle = null
    return {title, subtitle}
  }
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.currentUser
  }
}

export const RidePickerContainer = connect(
  mapStateToProps,
  null
)(RidePicker)
